import { InvalidPaginationTypeError } from '@takeshape/errors';
import { findSchemaAtPath, getReturnShape, hasArg } from '@takeshape/schema';
import { JSONPath as jsonPath } from 'jsonpath-plus';
import set from 'lodash/set.js';
import size from 'lodash/size.js';
/**
 * Converts a JSON path to an array of field names for schema/AST navigation.
 * Filters out array indices (e.g., [0], [*]) since:
 * - GraphQL AST doesn't have numeric field names
 * - Schema navigation auto-dereferences arrays via isArraySchema
 */
export function jsonPathToArray(path) {
    const array = jsonPath.toPathArray(path);
    // Keep only valid field names (start with letter or underscore, not pure digits)
    return array.filter((pathComponent) => /^[a-zA-Z_]\w*$/.test(pathComponent));
}
function setPaginationArg(args, { pageSize, pageSizeArg }) {
    if (pageSizeArg && pageSize !== undefined) {
        set(args, pageSizeArg, pageSize);
    }
}
export function pageBasedArgs(config) {
    let page = 0;
    return () => {
        page += 1;
        const args = set({}, config.pageArg, page);
        setPaginationArg(args, config);
        return args;
    };
}
export function pageBasedHasMore(totalPagesPath) {
    let page = 0;
    return (data) => {
        page += 1;
        const totalPages = jsonPath({
            json: data,
            path: totalPagesPath,
            wrap: false
        });
        return page < totalPages;
    };
}
export function cursorArgs({ cursorArg, cursorPath, pageSize, pageSizeArg }) {
    return (data) => {
        const args = set({}, pageSizeArg, pageSize);
        const cursor = jsonPath({ json: data, path: cursorPath, wrap: false });
        if (cursor) {
            set(args, cursorArg, cursor);
        }
        return args;
    };
}
export function cursorHasMore(hasMorePath) {
    return (data) => {
        const hasMore = jsonPath({ json: data, path: hasMorePath, wrap: false });
        return Boolean(hasMore);
    };
}
export function offsetArgs(config) {
    let soFar = 0;
    const { itemsPath, offsetArg } = config;
    return (data) => {
        const pageSize = size(jsonPath({ json: data, path: itemsPath, wrap: false }));
        soFar += pageSize;
        const args = set({}, offsetArg, soFar);
        setPaginationArg(args, config);
        return args;
    };
}
export function offsetHasMore(config) {
    let soFar = 0;
    const { itemTotalPath, itemsPath, pageSize } = config;
    return (data) => {
        // If there is no itemTotalPath we should fetch until the API returns no more items
        if (!itemTotalPath) {
            const pageSize = size(jsonPath({ json: data, path: itemsPath, wrap: false }));
            return pageSize !== 0;
        }
        const total = jsonPath({ json: data, path: itemTotalPath, wrap: false });
        const calculatedPageSize = pageSize ?? size(jsonPath({ json: data, path: itemsPath, wrap: false }));
        soFar += calculatedPageSize;
        return soFar < total;
    };
}
export function createPageHelper(config) {
    const { pageTotalPath, itemsPath } = config;
    return {
        getArgs: pageBasedArgs(config),
        hasMore: pageBasedHasMore(pageTotalPath),
        itemsPath
    };
}
export function createOffsetHelper(config) {
    const { itemsPath } = config;
    return {
        getArgs: offsetArgs(config),
        hasMore: offsetHasMore(config),
        itemsPath
    };
}
export function createCursorHelper(config) {
    return {
        getArgs: cursorArgs({
            ...config,
            pageSize: config.pageSize ?? 10
        }),
        hasMore: cursorHasMore(config.hasMorePath),
        itemsPath: config.itemsPath
    };
}
export function createPaginationHelper(config) {
    if (config.type === 'cursor') {
        return createCursorHelper(config);
    }
    if (config.type === 'offset') {
        return createOffsetHelper(config);
    }
    if (config.type === 'page') {
        return createPageHelper(config);
    }
    throw new InvalidPaginationTypeError(['cursor', 'offset', 'page']);
}
export function createUserDefinedPaginationHelper(queryConfig) {
    if (!queryConfig.pagination) {
        return undefined;
    }
    return createPaginationHelper(queryConfig.pagination);
}
export function getProvidedPaginationArgs(config) {
    let args = [];
    if (config.type === 'cursor') {
        args = [config.cursorArg, config.pageSizeArg];
    }
    else if (config.type === 'offset') {
        args = [config.offsetArg];
    }
    else if (config.type === 'page') {
        args = [config.pageArg];
    }
    return args;
}
export function getProvidedPaginationPaths(config) {
    let paths = [];
    if (config.type === 'cursor') {
        paths = [config.itemsPath, config.cursorPath, config.hasMorePath];
    }
    else if (config.type === 'offset') {
        paths = [config.itemsPath, config.itemTotalPath].filter(Boolean);
    }
    else if (config.type === 'page') {
        paths = [config.itemsPath, config.pageTotalPath];
    }
    return paths.map(jsonPathToArray);
}
export function createPredicate(config) {
    const requiredArgs = getProvidedPaginationArgs(config);
    const requiredPaths = getProvidedPaginationPaths(config);
    return (projectSchema, propOrQuery) => requiredArgs.every((arg) => hasArg(projectSchema, propOrQuery, arg)) &&
        requiredPaths.every((path) => {
            const returnShape = getReturnShape(projectSchema, propOrQuery);
            return returnShape && Boolean(findSchemaAtPath(projectSchema, returnShape.schema, path));
        });
}
/**
 * Get pagination argument names from config.
 * Returns the argument names that need to be added to the paginated field.
 */
export function getPaginationArgNames(config) {
    if (config.type === 'cursor') {
        return [config.cursorArg, config.pageSizeArg];
    }
    if (config.type === 'offset') {
        const args = [config.offsetArg];
        if (config.pageSizeArg) {
            args.push(config.pageSizeArg);
        }
        return args;
    }
    if (config.type === 'page') {
        const args = [config.pageArg];
        if (config.pageSizeArg) {
            args.push(config.pageSizeArg);
        }
        return args;
    }
    return [];
}
/**
 * Get field argument mappings for pagination.
 * Returns mappings suitable for addFieldArguments.
 */
export function getPaginationFieldArguments(config) {
    return getPaginationArgNames(config).map((name) => ({
        name,
        variableName: name
    }));
}
/**
 * Get the field paths that pagination needs to read from the response.
 * These are paths like "pageInfo.endCursor" that must be in the query's selection set.
 */
export function getPaginationMetadataFieldPaths(config) {
    if (config.type === 'cursor') {
        return [config.cursorPath, config.hasMorePath];
    }
    if (config.type === 'offset') {
        // itemTotalPath is optional for offset pagination
        return config.itemTotalPath ? [config.itemTotalPath] : [];
    }
    if (config.type === 'page') {
        return [config.pageTotalPath];
    }
    return [];
}
/**
 * Get variable type mappings from pagination config with fallback types.
 * Use this only when schema-based type lookup is not available.
 * @deprecated Prefer using getPaginationVariableTypesFromSchema when possible
 */
export function getPaginationVariableTypes(config) {
    const mappings = [];
    if (config.type === 'cursor') {
        mappings.push({ name: config.cursorArg, type: 'String' });
        mappings.push({ name: config.pageSizeArg, type: 'Int' });
    }
    else if (config.type === 'offset') {
        mappings.push({ name: config.offsetArg, type: 'Int' });
        if (config.pageSizeArg) {
            mappings.push({ name: config.pageSizeArg, type: 'Int' });
        }
    }
    else if (config.type === 'page') {
        mappings.push({ name: config.pageArg, type: 'Int' });
        if (config.pageSizeArg) {
            mappings.push({ name: config.pageSizeArg, type: 'Int' });
        }
    }
    return mappings;
}
